import type { ActionCenterSearchResult } from "~/server/api/action-center/search.get";
import type {
  ActionCenterOfferThreadMetadata,
  MessageThreadResponse,
} from "~/types/fight_offers.types";
import type { Tables } from "~/types/types.gen";
import { HttpMethod } from "~/utils/constants";

export interface MessageThread {
  id: number;
  recipientId: string;
  recipientName: string;
  recipientAvatarUrl: string | undefined;
  needsRefresh: boolean;
  totalMessageCountForThread: number | null;
  allMessagesLoadedForThread: boolean;
  newMessageThread: boolean;
  isFightOfferThread: boolean;
  messagesContent: Tables<"chats_messages">[];
  countOfUnreadMessages: number;

  // stores the content of the new message input field for this specific thread
  temporaryNewMessageStorage: string;
}

export const useMessagesStore = defineStore("messagesStore", {
  state: () => ({
    initialFetchFinished: false,
    messageThreads: [] as MessageThread[],
    selectedMessageThread: null as MessageThread | null,
    messageThreadsLoading: true,
    messageContentLoading: true,
    newMessageRequestInProgress: false,
    defaultPagingCount: 20,
    realtimeSubscriberDeinitDate: null as Date | null,
    unreadMessagesCount: 0 as number | undefined,
    lastUnreadMessagesCountFetchDate: null as Date | null,
    pushNotificationToastChatId: undefined as number | undefined,
    offerThreadMetadataByChatId: {} as Record<
      number,
      ActionCenterOfferThreadMetadata
    >,
  }),
  getters: {
    // selectedMessageThread!.messagesContent.filter((msg) => {
    //   return msg.read_at == null;
    // }).length > 0
  },
  actions: {
    getThreadById(threadId: number | null): MessageThread | undefined {
      if (threadId == null) {
        return undefined;
      }

      return this.messageThreads.find((thread) => thread.id == threadId);
    },

    async selectMessageThread(thread: MessageThread) {
      this.selectedMessageThread = thread;

      await this.refreshMessageThreadContentIfNeeded(thread);
      this.markAllMessagesInThreadAsRead();
    },

    async fetchMessageThreads(forceRefresh = false) {
      if (
        !forceRefresh &&
        (this.initialFetchFinished || this.messageThreads.length > 0)
      ) {
        return;
      }

      const entityId = useUserStore().usersCurrentEntity?.id;

      if (!entityId) {
        this.messageThreadsLoading = false;
        return;
      }

      this.messageThreadsLoading = true;
      const selectedThreadId = this.selectedMessageThread?.id;

      try {
        const data = await $fetch<MessageThreadResponse[]>(
          "/api/action-center/threads",
          {
            query: { entityId },
          },
        );

        this.initialFetchFinished = true;

        if (data != null) {
          this.messageThreads = [];

          data.forEach((messageThread) => {
            const lastMessageInThread = {
              id: messageThread.message_id,
              created_at: messageThread.message_created_at,
              chat_id: messageThread.chat_id,
              author_id: messageThread.message_author_id,
              content: messageThread.message_content,
              read_at: messageThread.message_read_at,
            } as Tables<"chats_messages">;

            const newMessageThread: MessageThread = {
              id: messageThread.chat_id,
              recipientId: messageThread.recipient_id,
              recipientName: messageThread.recipient_full_name,
              recipientAvatarUrl: messageThread.recipient_avatar_url,
              needsRefresh: false,
              totalMessageCountForThread: null,
              allMessagesLoadedForThread: false,
              messagesContent: [lastMessageInThread],
              newMessageThread: false,
              isFightOfferThread:
                !!this.offerThreadMetadataByChatId[messageThread.chat_id],
              countOfUnreadMessages: messageThread.unread_count,
              temporaryNewMessageStorage: "",
            };

            this.messageThreads.push(newMessageThread);
          });

          if (selectedThreadId != null) {
            const updatedSelection = this.messageThreads.find(
              (thread) => thread.id === selectedThreadId,
            );
            if (updatedSelection) {
              this.selectedMessageThread = updatedSelection;
            }
          }
        }

        await this.fetchOfferThreadMetadata();

        this.sortMessageThreadsByDate();
      } catch (error) {
        usePosthog().logError(
          error,
          "messages.ts:fetchMessageThreads()",
          "fetching message threads failed",
        );
        displayErrorNotification();
      } finally {
        this.messageThreadsLoading = false;
      }
    },

    async fetchOfferThreadMetadata() {
      const entityId = useUserStore().usersCurrentEntity?.id;
      if (!entityId) {
        return;
      }

      try {
        const data = await $fetch<ActionCenterOfferThreadMetadata[]>(
          "/api/action-center/offer-threads",
          {
            query: { entityId },
          },
        );

        const nextMap: Record<number, ActionCenterOfferThreadMetadata> = {};
        (data ?? []).forEach((metadata) => {
          nextMap[metadata.chat_id] = metadata;
        });
        this.offerThreadMetadataByChatId = nextMap;
        this.populateFightOfferThreadFlags();
      } catch (error) {
        usePosthog().logError(
          error,
          "messages.ts:fetchOfferThreadMetadata()",
          "fetching offer thread metadata failed",
        );
      }
    },

    getOfferMetadataForThread(
      threadId: number,
    ): ActionCenterOfferThreadMetadata | undefined {
      return this.offerThreadMetadataByChatId[threadId];
    },

    populateFightOfferThreadFlags() {
      const offerChatIds = new Set(
        Object.keys(this.offerThreadMetadataByChatId).map((key) => Number(key)),
      );

      this.messageThreads.forEach((thread) => {
        thread.isFightOfferThread = offerChatIds.has(thread.id);
      });
    },

    sortMessageThreadsByDate() {
      this.messageThreads = this.messageThreads.sort((a, b) => {
        const latestA =
          a.messagesContent.length > 0
            ? Math.max(
                ...a.messagesContent.map((obj) =>
                  new Date(obj.created_at).getTime(),
                ),
              )
            : -Infinity; // Handle empty nested array

        const latestB =
          b.messagesContent.length > 0
            ? Math.max(
                ...b.messagesContent.map((obj) =>
                  new Date(obj.created_at).getTime(),
                ),
              )
            : -Infinity; // Handle empty nested array

        return latestB - latestA; // Sort descending by latest date
      });
    },

    async fetchMessageContent(thread: MessageThread) {
      this.messageContentLoading = true;

      // Compute the last message date so that we load
      // only those messages that are older.
      let lastMessageDate = new Date().toISOString();

      if (thread.messagesContent) {
        const lastMessage = thread.messagesContent.at(-1);
        if (lastMessage) {
          lastMessageDate = lastMessage.created_at;
        }
      }

      const { data, error } = await useSupabaseClient()
        .from("chats_messages")
        .select("*")
        .eq("chat_id", thread.id)
        .order("created_at", { ascending: false }) // 'because we're using 'flex-col-reversed' for message content
        .lt("created_at", lastMessageDate)
        .limit(this.defaultPagingCount);

      if (error == null && data != null) {
        let messages = data as Tables<"chats_messages">[];

        // When fetching the first page we have to filter out the
        // first message because it is already in the list when
        // threads were fetched.
        if (thread.messagesContent.length > 0) {
          const firstMessageId = thread.messagesContent[0].id;
          if (firstMessageId) {
            messages = messages.filter((message) => {
              return message.id != firstMessageId;
            });
          }
        }

        thread.messagesContent.push(...messages);

        if (thread.totalMessageCountForThread == null) {
          this.updateThreadsTotalMessagesCount(thread);
        } else {
          thread.allMessagesLoadedForThread =
            thread.messagesContent.length == thread.totalMessageCountForThread;
        }
      } else {
        displayErrorNotification();
      }

      this.messageContentLoading = false;
    },

    async refreshMessageThreadContentIfNeeded(thread: MessageThread) {
      if (!thread.needsRefresh || this.realtimeSubscriberDeinitDate == null) {
        return;
      }

      // Fetch new messages. New messages are messages that were received
      // after the time that user left the /action-center page.
      const { data, error } = await useSupabaseClient()
        .from("chats_messages")
        .select("*")
        .eq("chat_id", thread.id)
        .order("created_at", { ascending: false })
        .gt("created_at", this.realtimeSubscriberDeinitDate.toISOString());

      if (error == null && data != null) {
        thread.needsRefresh = false;

        const newMessages = data as Tables<"chats_messages">[];

        // Either update content of the existing message or
        // push the new message in the existing message array.
        newMessages.forEach((newMessage) => {
          const existingMessage = thread.messagesContent.find(
            (message) => message.id == newMessage.id,
          );
          if (existingMessage) {
            existingMessage.content = newMessage.content;
          } else {
            thread.messagesContent.push(newMessage);
          }
        });

        // At this point the messagesContent array isn't sorted anymore.
        // Sort the array by 'created_at' property.
        thread.messagesContent = thread.messagesContent.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );

        await this.updateThreadsTotalMessagesCount(thread);

        thread.allMessagesLoadedForThread =
          thread.messagesContent.length == thread.totalMessageCountForThread;
      } else {
        displayErrorNotification();
        console.log(error);
      }
    },

    async updateThreadsTotalMessagesCount(thread: MessageThread) {
      const { count } = await useSupabaseClient()
        .from("chats_messages")
        .select("*", { count: "exact", head: true })
        .eq("chat_id", thread.id);

      if (count) {
        thread.totalMessageCountForThread = count;

        thread.allMessagesLoadedForThread =
          thread.messagesContent.length == thread.totalMessageCountForThread;
      }
    },

    async sendMessage(content: string): Promise<boolean> {
      let chatId = this.selectedMessageThread?.id;
      const trimmedContent = content.trim();
      const entityId = useUserStore().usersCurrentEntity?.id;

      if (trimmedContent.length == 0 || entityId == null) {
        return false;
      }

      this.newMessageRequestInProgress = true;

      if (this.selectedMessageThread?.newMessageThread) {
        chatId = await this.insertNewMessageThread({
          id: this.selectedMessageThread?.recipientId,
          name: this.selectedMessageThread?.recipientName,
          avatar_url: this.selectedMessageThread?.recipientAvatarUrl ?? null,
        });
      }

      if (!chatId) {
        this.newMessageRequestInProgress = false;
        return false;
      }

      try {
        const data = await $fetch<Tables<"chats_messages">>(
          "/api/action-center/message",
          {
            method: HttpMethod.POST,
            body: {
              entityId,
              chatId,
              recipientId: this.selectedMessageThread?.recipientId,
              content: content.trim(),
            },
          },
        );

        this.newMessageRequestInProgress = false;
        this.selectedMessageThread?.messagesContent.unshift(data);
        this.sortMessageThreadsByDate();
        return true;
      } catch (error) {
        this.newMessageRequestInProgress = false;
        usePosthog().logError(
          error,
          "messages.ts:sendMessage()",
          "sending message failed",
        );
      }
      displayErrorNotification();
      return false;
    },

    async insertNewMessageThread(recipient: {
      id: string;
      name: string | null;
      avatar_url: string | null;
    }): Promise<number | undefined> {
      try {
        const entityId = useUserStore().usersCurrentEntity?.id;

        if (!entityId) {
          throw new Error("User entity ID is not available");
        }

        const response = await $fetch<{ chatId: number }>(
          "/api/action-center/thread",
          {
            method: HttpMethod.POST,
            body: {
              recipientId: recipient.id,
              entityId: entityId,
            },
          },
        );

        if (response?.chatId) {
          const newThread: MessageThread = {
            id: response.chatId,
            recipientId: recipient.id,
            recipientName: recipient.name ?? "",
            recipientAvatarUrl: recipient.avatar_url ?? undefined,
            needsRefresh: false,
            totalMessageCountForThread: 0,
            allMessagesLoadedForThread: true,
            newMessageThread: false,
            isFightOfferThread: false,
            messagesContent: [],
            countOfUnreadMessages: 0,
            temporaryNewMessageStorage: "",
          };

          this.messageThreads.unshift(newThread);
          this.selectedMessageThread = newThread;
          this.sortMessageThreadsByDate();

          return response.chatId;
        }
      } catch (error) {
        usePosthog().logError(
          error,
          "messages.ts:insertNewMessageThread()",
          "creating new message thread failed",
        );
        displayErrorNotification();
      }

      return undefined;
    },

    async getUsersForQuery(q: string): Promise<ActionCenterSearchResult[]> {
      const queryTrimmed = q.trim();

      if (!queryTrimmed) {
        return [];
      }

      try {
        const data = await $fetch<ActionCenterSearchResult[]>(
          "/api/action-center/search",
          {
            query: { q: queryTrimmed },
          },
        );

        return data || [];
      } catch (error) {
        usePosthog().logError(
          error,
          "messages.ts:getUsersForQuery()",
          "fetching users for message search failed",
        );

        return [];
      }
    },

    async removeSelectedMessageThread() {
      const messageThreadId = this.selectedMessageThread?.id;

      if (!messageThreadId) {
        return;
      }

      const { error } = await useSupabaseClient()
        .from("chats")
        .delete()
        .eq("id", messageThreadId);

      if (error == null) {
        this.messageThreads = this.messageThreads.filter(
          (thread) => thread.id !== messageThreadId,
        );

        if (this.messageThreads.length > 0) {
          this.selectMessageThread(this.messageThreads[0]);
        } else {
          this.selectedMessageThread = null;
        }

        return;
      }

      displayErrorNotification();
    },

    async markAllMessagesInThreadAsRead() {
      const entityId = useUserStore().usersCurrentEntity?.id;

      if (entityId == null || this.selectedMessageThread == undefined) {
        return;
      }

      // Wait for the messages to be loaded because we need the message IDs to mark them as read.
      if (this.messageContentLoading) {
        await new Promise((resolve) => {
          const checkLoadingInterval = setInterval(() => {
            if (!this.messageContentLoading) {
              clearInterval(checkLoadingInterval);
              resolve(true);
            }
          }, 50);
        });
      }

      const currentDate = new Date().toISOString();

      const unreadMessages = this.selectedMessageThread.messagesContent.filter(
        (msg) => {
          return (
            msg.read_at == null &&
            msg.author_id === this.selectedMessageThread?.recipientId
          );
        },
      );

      if (unreadMessages.length > 0) {
        // update local state
        unreadMessages.forEach((msg) => {
          msg.read_at = currentDate;
        });

        if (this.unreadMessagesCount) {
          this.unreadMessagesCount -=
            this.selectedMessageThread.countOfUnreadMessages;
        }

        this.selectedMessageThread.countOfUnreadMessages = 0;

        try {
          await $fetch("/api/action-center/read-thread", {
            method: HttpMethod.POST,
            body: {
              entityId,
              chatId: this.selectedMessageThread.id,
            },
          });
        } catch {
          // revert the local state because something went wrong
          unreadMessages.forEach((msg) => {
            msg.read_at = null;
          });

          this.selectedMessageThread.countOfUnreadMessages =
            unreadMessages.length;
        }
      }
    },

    async getUnreadMessagesCount(): Promise<number | undefined> {
      if (!useUserStore().isUserLoggedIn) {
        return 0;
      }

      const entityId = useUserStore().usersCurrentEntity?.id;
      if (entityId == null) {
        return this.unreadMessagesCount;
      }

      // Only fetch if at least 5 seconds have passed from the last fetch.
      const currentDateSeconds = new Date().getTime() / 1000;
      const lastFetchDate = this.lastUnreadMessagesCountFetchDate;
      if (lastFetchDate) {
        const lastFetchDateSeconds = lastFetchDate.getTime() / 1000;
        if (currentDateSeconds - lastFetchDateSeconds < 5) {
          return this.unreadMessagesCount;
        }
      }

      try {
        const data = await $fetch<Array<{ chat_id: number }>>(
          "/api/action-center/unread-messages-count",
          {
            query: { entityId: entityId },
          },
        );

        this.lastUnreadMessagesCountFetchDate = new Date();
        this.unreadMessagesCount = data.length;

        const countMap = data.reduce((map, num) => {
          map[num.chat_id] = (map[num.chat_id] || 0) + 1; // Increment the count if exists, or initialize to 1
          return map;
        }, {});

        Object.entries(countMap).forEach(([key, value]) => {
          const thread = this.messageThreads.find((thread) => {
            return thread.id == Number(key);
          });

          if (thread) {
            thread.countOfUnreadMessages = Number(value);
          }
        });

        return data.length;
      } catch (error) {
        if (process.client) {
          usePosthog().logError(
            error,
            "messages.ts:getUnreadMessagesCount()",
            "fetching unread messages count failed",
          );
        }

        return this.unreadMessagesCount;
      }
    },
  },
});
